/**
 * src/lib/api.ts
 *
 * Backend API への fetch ラッパー（Web UI 用）。
 *
 * 設計方針:
 * - cookie 経由認証（credentials: "include"）。X-API-Key は Web UI で使わない
 * - 統一レスポンス { ok: true, data } / { ok: false, error } を ApiError に正規化
 * - 401 は呼び出し側で「未ログイン」と判断できるよう code: "UNAUTHORIZED" でそろえる
 * - dev 時は Vite proxy 経由で /api → localhost:3000、本番は Caddy 同一オリジン
 *
 * このファイル:
 * - Endpoint 仕様は backend/src/api/routes に従う（/api/auth/login, /api/auth/logout, /api/me）
 * - PII（email / password）はログ / console.error に絶対出さない
 */

const BASE_PATH = "/api";
const API_CACHE_NAME = "ai-os-api-v1";

export interface ApiUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
}

export type UserRole = "admin" | "user";

export interface AdminUser {
  id: string;
  email: string;
  name: string | null;
  role: UserRole;
  created_at: string;
  deleted_at: string | null;
}

export interface CreateAdminUserInput {
  email: string;
  name: string;
  password: string;
  role: UserRole;
}

export interface AdminApiKey {
  id: string;
  company_id: string;
  name: string;
  key_prefix: string;
  scope: string;
  last_used_at: string | null;
  revoked_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface AdminLineChannel {
  id: string;
  company_id: string;
  name: string;
  destination: string | null;
  default_assignee_user_id: string | null;
  add_friend_url: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface CompanyEmailTemplate {
  default_email_opening: string | null;
  default_email_closing: string | null;
  default_email_reply_opening: string | null;
  default_email_signature: string | null;
}

export interface CompanyProfile {
  name: string;
  display_name: string | null;
  representative: string | null;
  postal_code: string | null;
  address: string | null;
  phone: string | null;
  fax: string | null;
  email: string | null;
  license_number: string | null;
  bank_info: string | null;
  logo_url: string | null;
  default_email_opening: string | null;
  default_email_closing: string | null;
  default_email_reply_opening: string | null;
}

export interface UpdateCompanyInput {
  name?: string;
  display_name?: string | null;
  representative?: string | null;
  postal_code?: string | null;
  address?: string | null;
  phone?: string | null;
  fax?: string | null;
  email?: string | null;
  license_number?: string | null;
  bank_info?: string | null;
  logo_url?: string | null;
  default_email_opening?: string | null;
  default_email_closing?: string | null;
  default_email_reply_opening?: string | null;
}

export interface UpdateCompanyEmailTemplateInput {
  default_email_opening?: string | null;
  default_email_closing?: string | null;
  default_email_reply_opening?: string | null;
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: ReadonlyArray<{ path: string; message: string }> | undefined;

  constructor(
    code: string,
    message: string,
    status: number,
    details?: ReadonlyArray<{ path: string; message: string }>,
  ) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

interface OkBody<T> {
  ok: true;
  data: T;
}
interface ErrBody {
  ok: false;
  error: {
    code: string;
    message: string;
    details?: ReadonlyArray<{ path: string; message: string }>;
  };
}

async function request<T>(
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers["content-type"] = "application/json";

  const init: RequestInit = {
    method,
    headers,
    credentials: "include",
  };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }

  let res: Response;
  try {
    res = await fetch(`${BASE_PATH}${path}`, init);
  } catch (e) {
    // ネットワーク到達不可
    const msg = e instanceof Error ? e.message : "network error";
    throw new ApiError("NETWORK_ERROR", msg, 0);
  }

  // 204 No Content / レスポンスボディ無し
  if (res.status === 204) {
    return undefined as T;
  }

  let bodyJson: OkBody<T> | ErrBody;
  try {
    bodyJson = (await res.json()) as OkBody<T> | ErrBody;
  } catch {
    throw new ApiError("INVALID_RESPONSE", "invalid JSON body", res.status);
  }

  if (bodyJson.ok === true) {
    return bodyJson.data;
  }

  if (
    res.status === 401 &&
    typeof window !== "undefined" &&
    window.location.pathname !== "/login"
  ) {
    window.location.assign("/login");
  }

  throw new ApiError(
    bodyJson.error.code,
    bodyJson.error.message,
    res.status,
    bodyJson.error.details,
  );
}

// ─── Auth ─────────────────────────────────────────────────

export async function login(email: string, password: string): Promise<{ user: ApiUser }> {
  await clearApiResponseCache();
  return request("POST", "/auth/login", { email, password });
}

export async function logout(): Promise<void> {
  try {
    await request<{ ok: boolean }>("POST", "/auth/logout");
  } finally {
    await clearApiResponseCache();
  }
}

export async function fetchMe(): Promise<{ user: ApiUser | null }> {
  return request("GET", "/me");
}

export interface MySignature {
  email_signature: string | null;
}

export async function getMySignature(): Promise<MySignature> {
  return request("GET", "/me/signature");
}

export async function updateMySignature(value: string): Promise<MySignature> {
  const email_signature = value.trim() === "" ? null : value;
  return request("PATCH", "/me/signature", { email_signature });
}

async function clearApiResponseCache(): Promise<void> {
  if (typeof window === "undefined" || !("caches" in window)) return;
  await window.caches.delete(API_CACHE_NAME);
}

// ─── Domain types (Phase 2-3 read-only) ──────────────────
// Backend Service が返す Prisma レコードに対し、UI で必要なフィールドだけを宣言する。
// 余剰フィールドは any 化せず unknown に倒し、表示する物だけ readonly で型付け。

export interface ApiCustomer {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  line_id: string | null;
  tags?: string | null;
  memo: string | null;
  created_at: string;
  updated_at: string;
}

export interface ApiUnlinkedLineUser {
  id: string;
  line_user_id: string;
  display_name: string | null;
  picture_url: string | null;
  status_message: string | null;
  followed_at: string;
  unfollowed_at: string | null;
  linked_at: string | null;
  linked_customer_id: string | null;
}

export type CaseStatus =
  | "open"
  | "negotiating"
  | "mediation"
  | "contracting"
  | "closed"
  | "lost"
  | "on_hold";
export type CaseType = "sale" | "purchase" | "rental" | "management" | "other";
export type MediationType = "exclusive_exclusive" | "exclusive" | "general";

export interface ApiCase {
  id: string;
  title: string;
  status: CaseStatus;
  case_type: CaseType;
  property_address: string | null;
  summary: string | null;
  memo: string | null;
  mediation_type: MediationType | null;
  mediation_start_date: string | null;
  mediation_end_date: string | null;
  created_at: string;
}

export type TaskStatus = "open" | "in_progress" | "done" | "cancelled";
export type TaskPriority = "high" | "medium" | "low";
export type TaskCategory = "business" | "internal";

export interface ApiTask {
  id: string;
  title: string;
  description: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  category: TaskCategory;
  case_id: string | null;
  customer_id: string | null;
  due_date: string | null;
  assigned_to: string | null;
  google_event_id: string | null;
  created_at: string;
}

export type ScheduleType = "meeting" | "survey" | "contract" | "inspection" | "other";

export interface ApiSchedule {
  id: string;
  case_id: string;
  type: ScheduleType;
  title: string;
  scheduled_at: string;
  location: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export type HistoryType = "meeting" | "call" | "email" | "line" | "memo" | "other";

export interface ApiHistory {
  id: string;
  case_id: string;
  type: HistoryType;
  content: string;
  ai_summary: string | null;
  next_action: string | null;
  created_at: string;
}

export type MessageChannel = "mail" | "line" | "sms" | "other";
export type MessageDirection = "inbound" | "outbound";

export interface ApiMessage {
  id: string;
  case_id: string;
  channel: MessageChannel;
  direction: MessageDirection;
  sender: string | null;
  recipient: string | null;
  subject: string | null;
  content: string;
  ai_summary: string | null;
  external_id: string | null;
  thread_id: string | null;
  in_reply_to: string | null;
  created_at: string;
  updated_at: string;
}

export interface ApiCrossMessage {
  id: string;
  case_id: string | null;
  customer_id: string | null;
  case_title: string | null;
  customer_name: string | null;
  channel: MessageChannel;
  direction: MessageDirection;
  sender: string | null;
  recipient: string | null;
  subject: string | null;
  content: string;
  ai_summary: string | null;
  mail_type: string;
  bulk_score: number;
  scrub_after: string | null;
  scrubbed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ApiUnlinkedMessage {
  id: string;
  case_id: string | null;
  customer_id: string | null;
  channel: MessageChannel;
  direction: MessageDirection;
  sender: string | null;
  recipient: string | null;
  subject: string | null;
  content: string;
  ai_summary: string | null;
  external_id: string | null;
  thread_id: string | null;
  in_reply_to: string | null;
  created_at: string;
  updated_at: string;
}

export interface ListAllMessagesFilter {
  limit?: number;
  channel?: MessageChannel | undefined;
  direction?: MessageDirection | undefined;
}

export type PropertyType = "land" | "house" | "condo" | "building" | "other";
export type SurveyStatus = "confirmed" | "current" | "none";

export interface ApiProperty {
  id: string;
  case_id: string;
  property_type: PropertyType;
  address: string | null;
  price: number | null;
  land_area: number | null;
  building_area: number | null;
  layout: string | null;
  built_year: number | null;
  nearest_station: string | null;
  survey_status: SurveyStatus | null;
  survey_cost: number | null;
  memo: string | null;
  created_at: string;
  updated_at: string;
}

export interface UpsertPropertyInput {
  property_type: PropertyType;
  address?: string | null | undefined;
  price?: number | null | undefined;
  land_area?: number | null | undefined;
  building_area?: number | null | undefined;
  layout?: string | null | undefined;
  built_year?: number | null | undefined;
  nearest_station?: string | null | undefined;
  survey_status?: SurveyStatus | null | undefined;
  survey_cost?: number | null | undefined;
  memo?: string | null | undefined;
}

export interface ApiAttachment {
  id: string;
  case_id: string;
  file_name: string;
  mime_type: string;
  file_size: number;
  uploaded_by: string | null;
  created_at: string;
}

export interface GmailSendResult {
  message_id: string;
  thread_id: string;
  external_id: string;
}

export type ContactType =
  | "judicial_scrivener"
  | "lawyer"
  | "surveyor"
  | "land_house_investigator"
  | "builder"
  | "demolisher"
  | "mgmt_company"
  | "bank"
  | "agent"
  | "other";

export interface ApiCaseContact {
  id: string;
  case_id: string;
  name: string;
  type: ContactType;
  company: string | null;
  phone: string | null;
  email: string | null;
  memo: string | null;
  created_at: string;
}

export type CaseCustomerRole =
  | "seller"
  | "buyer"
  | "co_seller"
  | "co_buyer"
  | "co_owner"
  | "heir"
  | "owner"
  | "other";

export interface ApiCaseCustomer {
  id: string;
  case_id: string;
  customer_id: string;
  role: CaseCustomerRole;
  created_at: string;
  customer?: ApiCustomer;
}

export interface ApiCaseSummaryCustomer {
  id: string;
  name: string;
  role: CaseCustomerRole;
}

export interface ApiCaseSummary {
  case: ApiCase;
  customers: ReadonlyArray<ApiCaseSummaryCustomer>;
  case_customers: ReadonlyArray<ApiCaseSummaryCustomer>;
  contacts: ReadonlyArray<ApiCaseContact>;
  histories: ReadonlyArray<ApiHistory>;
  tasks: ReadonlyArray<ApiTask>;
  counts: {
    contacts_count: number;
    histories_count: number;
    open_tasks_count: number;
    overdue_tasks_count: number;
  };
}

export interface ApiAuditLog {
  id: string;
  user_id: string | null;
  action: "create" | "update" | "delete";
  resource: string;
  resource_id: string | null;
  diff: string | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

export interface AuditLogsResult {
  rows: ReadonlyArray<ApiAuditLog>;
  total: number;
}

export interface ApiStaleCase {
  case_id: string;
  title: string;
  status: string;
  property_address: string | null;
  last_contact_at: string;
  days_since_contact: number;
  has_history: boolean;
}

// ─── Read endpoints ──────────────────────────────────────

interface SearchCustomersParams {
  /** 横断 partial-match (name/phone/email/memo/tags/line_id を OR で contains) */
  q?: string;
  name?: string;
  phone?: string;
  email?: string;
  limit?: number;
  offset?: number;
}

type QueryValue = string | number | boolean | ReadonlyArray<string> | undefined;

function buildQuery(params: { [key: string]: QueryValue }): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === "") continue;
    if (Array.isArray(v)) {
      for (const item of v) sp.append(k, item);
      continue;
    }
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s.length > 0 ? `?${s}` : "";
}

export async function searchCustomers(
  params: SearchCustomersParams,
): Promise<ReadonlyArray<ApiCustomer>> {
  return request("GET", `/customers/search${buildQuery({ ...params })}`);
}

export async function listUnlinkedLineUsers(): Promise<ReadonlyArray<ApiUnlinkedLineUser>> {
  return request("GET", "/line-users");
}

export async function linkLineUserToCustomer(id: string, customerId: string): Promise<void> {
  await request<undefined>("POST", `/line-users/${encodeURIComponent(id)}/link`, {
    customer_id: customerId,
  });
}

export async function promoteLineUserToCustomer(
  id: string,
  input: { name: string; phone?: string; email?: string },
): Promise<ApiCustomer> {
  return request("POST", `/line-users/${encodeURIComponent(id)}/promote`, input);
}

export async function getCustomerById(customerId: string): Promise<ApiCustomer> {
  // TODO: replace this with GET /api/customers/:id when backend adds a dedicated endpoint.
  const customers = await searchCustomers({ q: customerId, limit: 50 });
  const exact = customers.find((customer) => customer.id === customerId);
  if (exact !== undefined) return exact;
  const paged = await findCustomerByIdFromSearchPages(customerId);
  if (paged !== undefined) return paged;
  throw new ApiError("NOT_FOUND", "customer not found", 404);
}

async function findCustomerByIdFromSearchPages(
  customerId: string,
): Promise<ApiCustomer | undefined> {
  const limit = 50;
  for (let offset = 0; offset <= 10_000; offset += limit) {
    const customers = await searchCustomers({ limit, offset });
    const exact = customers.find((customer) => customer.id === customerId);
    if (exact !== undefined) return exact;
    if (customers.length < limit) return undefined;
  }
  return undefined;
}

interface SearchCasesParams {
  q?: string;
  title?: string;
  customer_id?: string;
  status?: CaseStatus;
  case_type?: CaseType;
  limit?: number;
}

export async function searchCases(params: SearchCasesParams): Promise<ReadonlyArray<ApiCase>> {
  return request("GET", `/cases/search${buildQuery({ ...params })}`);
}

export interface GlobalSearchResult {
  cases: ReadonlyArray<ApiCase>;
  customers: ReadonlyArray<ApiCustomer>;
}

export async function searchGlobal(q: string, limit = 6): Promise<GlobalSearchResult> {
  const [cases, customers] = await Promise.all([
    searchCases({ q, limit }),
    searchCustomers({ q, limit }),
  ]);
  return { cases, customers };
}

export interface DashboardTodayTasks {
  incomplete: number;
  done: number;
  total: number;
}

const ACTIVE_CASE_STATUSES: ReadonlyArray<CaseStatus> = [
  "open",
  "negotiating",
  "mediation",
  "contracting",
];

export async function getDashboardTodayTasks(): Promise<DashboardTodayTasks> {
  const range = tokyoDayRange(new Date());
  const result = await searchTasks({
    statuses: ["done", "open", "in_progress"],
    date_from: range.start,
    date_to: range.end,
    limit: 100,
  });
  const done = result.rows.filter((task) => task.status === "done").length;
  const incomplete = result.rows.filter(isIncompleteTask).length;
  return { incomplete, done, total: result.rows.length };
}

export async function getDashboardOverdueTasks(): Promise<number> {
  const result = await searchTasks({
    statuses: ["open", "in_progress"],
    date_to: new Date().toISOString(),
    limit: 100,
  });
  return result.total;
}

export async function getDashboardActiveCases(): Promise<number> {
  const results = await Promise.all(
    ACTIVE_CASE_STATUSES.map((status) => searchCases({ status, limit: 50 })),
  );
  return uniqueById(results.flat()).length;
}

export async function getDashboardRecentInboundMessages(): Promise<number> {
  const cases = await searchCases({ limit: 50 });
  const lists = await Promise.all(cases.map((caseRecord) => listMessages(caseRecord.id)));
  const threshold = Date.now() - 24 * 60 * 60 * 1000;
  return lists.flat().filter((message) => isRecentInboundMessage(message, threshold)).length;
}

export async function getCaseSummary(caseId: string): Promise<ApiCaseSummary> {
  return request("GET", `/cases/${encodeURIComponent(caseId)}/summary`);
}

export async function getProperty(caseId: string): Promise<ApiProperty | null> {
  return request("GET", `/cases/${encodeURIComponent(caseId)}/property`);
}

export async function upsertProperty(
  caseId: string,
  input: UpsertPropertyInput,
): Promise<ApiProperty> {
  return request("PUT", `/cases/${encodeURIComponent(caseId)}/property`, input);
}

export async function deleteProperty(caseId: string): Promise<void> {
  await request<undefined>("DELETE", `/cases/${encodeURIComponent(caseId)}/property`);
}

export async function listAttachments(caseId: string): Promise<ReadonlyArray<ApiAttachment>> {
  return request("GET", `/cases/${encodeURIComponent(caseId)}/attachments`);
}

export async function uploadAttachment(caseId: string, file: File): Promise<ApiAttachment> {
  const form = new FormData();
  form.append("file", file);
  const path = `/cases/${encodeURIComponent(caseId)}/attachments`;
  let res: Response;
  try {
    res = await fetch(`${BASE_PATH}${path}`, {
      method: "POST",
      body: form,
      credentials: "include",
    });
  } catch (e) {
    const msg = e instanceof Error ? e.message : "network error";
    throw new ApiError("NETWORK_ERROR", msg, 0);
  }
  if (!res.ok) throw await buildApiError(res);
  let json: OkBody<ApiAttachment> | ErrBody;
  try {
    json = (await res.json()) as OkBody<ApiAttachment> | ErrBody;
  } catch {
    throw new ApiError("INVALID_RESPONSE", "invalid JSON body", res.status);
  }
  if (json.ok === true) return json.data;
  throw new ApiError(json.error.code, json.error.message, res.status, json.error.details);
}

export function attachmentContentUrl(id: string): string {
  return `${BASE_PATH}/attachments/${encodeURIComponent(id)}/content`;
}

export async function deleteAttachment(id: string): Promise<void> {
  await request<undefined>("DELETE", `/attachments/${encodeURIComponent(id)}`);
}

async function buildApiError(res: Response): Promise<ApiError> {
  if (
    res.status === 401 &&
    typeof window !== "undefined" &&
    window.location.pathname !== "/login"
  ) {
    window.location.assign("/login");
  }
  try {
    const bodyJson = (await res.json()) as ErrBody;
    if (bodyJson.ok === false) {
      return new ApiError(
        bodyJson.error.code,
        bodyJson.error.message,
        res.status,
        bodyJson.error.details,
      );
    }
  } catch {
    return new ApiError("INVALID_RESPONSE", "invalid JSON body", res.status);
  }
  return new ApiError("HTTP_ERROR", "request failed", res.status);
}

interface TodayTasksParams {
  limit?: number;
  include_overdue?: boolean;
  statuses?: ReadonlyArray<"open" | "in_progress">;
  category?: TaskCategory | undefined;
}

export async function getTodayTasks(
  params: TodayTasksParams = {},
): Promise<ReadonlyArray<ApiTask>> {
  const query = buildQuery({
    limit: params.limit,
    include_overdue: params.include_overdue,
    statuses: params.statuses,
    category: params.category,
  });
  return request("GET", `/today/tasks${query}`);
}

export async function listFollowUps(): Promise<ReadonlyArray<ApiStaleCase>> {
  return request("GET", "/follow-ups");
}

export async function getOverdueTaskCount(): Promise<number> {
  const tasks = await getTodayTasks({
    include_overdue: true,
    statuses: ["open", "in_progress"],
  });
  const now = new Date();
  return tasks.filter((task) => isPastDueDate(task.due_date, now)).length;
}

export interface SearchTasksParams {
  statuses?: ReadonlyArray<ApiTask["status"]> | undefined;
  case_id?: string | undefined;
  customer_id?: string | undefined;
  category?: TaskCategory | undefined;
  date_from?: string | undefined;
  date_to?: string | undefined;
  q?: string | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
}

export async function searchTasks(
  params: SearchTasksParams,
): Promise<{ rows: ReadonlyArray<ApiTask>; total: number }> {
  const query = buildQuery({
    statuses: params.statuses === undefined ? undefined : params.statuses.join(","),
    case_id: params.case_id,
    customer_id: params.customer_id,
    category: params.category,
    date_from: params.date_from,
    date_to: params.date_to,
    q: params.q,
    limit: params.limit,
    offset: params.offset,
  });
  return request("GET", `/tasks${query}`);
}

export async function listCustomerTasks(customerId: string): Promise<ReadonlyArray<ApiTask>> {
  return request("GET", `/customers/${encodeURIComponent(customerId)}/tasks`);
}

export async function listCustomerMessages(customerId: string): Promise<ReadonlyArray<ApiMessage>> {
  return request("GET", `/customers/${encodeURIComponent(customerId)}/messages`);
}

function isPastDueDate(iso: string | null, referenceDate: Date): boolean {
  if (iso === null) return false;
  const dueDate = new Date(iso);
  if (Number.isNaN(dueDate.getTime())) return false;
  return dueDate.getTime() < referenceDate.getTime();
}

function isIncompleteTask(task: ApiTask): boolean {
  return task.status === "open" || task.status === "in_progress";
}

function isRecentInboundMessage(message: ApiMessage, threshold: number): boolean {
  if (message.direction !== "inbound") return false;
  const createdAt = Date.parse(message.created_at);
  return !Number.isNaN(createdAt) && createdAt >= threshold;
}

function uniqueById<T extends { id: string }>(items: ReadonlyArray<T>): ReadonlyArray<T> {
  return Array.from(new Map(items.map((item) => [item.id, item])).values());
}

function tokyoDayRange(date: Date): { start: string; end: string } {
  const parts = tokyoDateParts(date);
  return {
    start: `${parts.year}-${parts.month}-${parts.day}T00:00:00.000+09:00`,
    end: `${parts.year}-${parts.month}-${parts.day}T23:59:59.999+09:00`,
  };
}

function tokyoDateParts(date: Date): { year: string; month: string; day: string } {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const values = { year: "1970", month: "01", day: "01" };
  for (const part of formatter.formatToParts(date)) {
    if (part.type === "year" || part.type === "month" || part.type === "day") {
      values[part.type] = part.value;
    }
  }
  return values;
}

export interface ListAuditLogsParams {
  user_id?: string;
  resource?: string;
  resource_id?: string;
  action?: "create" | "update" | "delete";
  start_date?: string;
  end_date?: string;
  limit?: number;
  offset?: number;
}

export async function listAuditLogs(params: ListAuditLogsParams): Promise<AuditLogsResult> {
  return request("GET", `/audit-logs${buildQuery({ ...params })}`);
}

export async function listAdminUsers(): Promise<{ users: AdminUser[] }> {
  return request("GET", "/admin/users");
}

export async function createAdminUser(input: CreateAdminUserInput): Promise<ApiUser> {
  return request("POST", "/admin/users", input);
}

export async function updateAdminUserRole(id: string, role: UserRole): Promise<AdminUser> {
  return request("PATCH", `/admin/users/${encodeURIComponent(id)}`, { role });
}

export interface UpdateAdminUserInput {
  name?: string;
  role?: UserRole;
}

export async function updateAdminUser(id: string, input: UpdateAdminUserInput): Promise<AdminUser> {
  return request("PATCH", `/admin/users/${encodeURIComponent(id)}`, input);
}

export async function listAdminApiKeys(): Promise<{ api_keys: AdminApiKey[] }> {
  return request("GET", "/admin/api-keys");
}

export async function issueAdminApiKey(input: {
  name: string;
  scope?: "all";
}): Promise<{ api_key: string; record: AdminApiKey }> {
  return request("POST", "/admin/api-keys", input);
}

export async function revokeAdminApiKey(id: string): Promise<AdminApiKey> {
  return request("DELETE", `/admin/api-keys/${encodeURIComponent(id)}`);
}

export async function deleteAdminApiKey(id: string): Promise<AdminApiKey> {
  return request("DELETE", `/admin/api-keys/${encodeURIComponent(id)}?hard=true`);
}

export async function getAdminApiKeySecret(id: string): Promise<{ secret: string }> {
  return request("GET", `/admin/api-keys/${encodeURIComponent(id)}/secret`);
}

export async function getCompany(): Promise<CompanyProfile> {
  return request("GET", "/company");
}

export async function updateCompany(input: UpdateCompanyInput): Promise<CompanyProfile> {
  return request("PATCH", "/admin/company", input);
}

// ─── 登記情報 (touki) 承認 ─────────────────────────────────
export type ToukiKind = "land" | "building" | "both";
export type ToukiDetail = "full" | "owner";
export type ToukiDeliveryMode = "attach_to_case" | "download_only";
export type ToukiStatus =
  | "pending_approval"
  | "approved"
  | "running"
  | "done"
  | "failed"
  | "rejected";

export interface AdminToukiRequest {
  id: string;
  // download_only(案件に紐付けない依頼) のときは null
  case_id: string | null;
  delivery_mode: ToukiDeliveryMode;
  kind: ToukiKind;
  detail: ToukiDetail;
  include_collateral: boolean;
  status: ToukiStatus;
  target: string;
  estimated_fee_yen: number;
  requested_by: string | null;
  summary: string | null;
  error_message: string | null;
  result_attachment_ids: string[] | null;
  created_at: string;
}

export async function listAdminToukiRequests(
  status: ToukiStatus | "all" = "pending_approval",
): Promise<{ touki_requests: AdminToukiRequest[] }> {
  return request("GET", `/admin/touki/requests?status=${encodeURIComponent(status)}`);
}

export async function approveToukiRequest(id: string): Promise<AdminToukiRequest> {
  return request("POST", `/admin/touki/requests/${encodeURIComponent(id)}/approve`);
}

export async function rejectToukiRequest(id: string): Promise<AdminToukiRequest> {
  return request("POST", `/admin/touki/requests/${encodeURIComponent(id)}/reject`);
}

export async function registerToukiAccount(input: {
  login_id: string;
  password: string;
}): Promise<{ id: string; status: string }> {
  return request("POST", "/admin/touki/accounts", input);
}

// 完了済み依頼の PDF をまとめた ZIP の DL リンク発行 (url は署名付き・7日有効)
export interface ToukiBundleResult {
  bundle: {
    id: string;
    file_name: string;
    file_count: number;
    total_size: number;
    expires_at: string;
    url: string;
  };
  included: Array<{ touki_request_id: string; target: string; file_count: number }>;
  skipped: Array<{ touki_request_id: string; reason: string }>;
}

export async function createToukiBundle(input: {
  touki_request_ids: string[];
}): Promise<ToukiBundleResult> {
  return request("POST", "/touki/bundles", input);
}

export async function getEmailTemplate(): Promise<CompanyEmailTemplate> {
  return request("GET", "/admin/email-template");
}

export async function updateEmailTemplate(
  input: UpdateCompanyEmailTemplateInput,
): Promise<CompanyEmailTemplate> {
  return request("PATCH", "/admin/email-template", input);
}

export async function listAdminLineChannels(): Promise<{
  line_channels: AdminLineChannel[];
}> {
  return request("GET", "/admin/line-channels");
}

export async function createAdminLineChannel(input: {
  name: string;
  destination?: string;
  channel_secret?: string;
  channel_access_token?: string;
  add_friend_url?: string;
}): Promise<AdminLineChannel> {
  return request("POST", "/admin/line-channels", input);
}

export async function updateAdminLineChannel(
  id: string,
  input: {
    name?: string;
    destination?: string;
    channel_secret?: string;
    channel_access_token?: string;
    add_friend_url?: string;
  },
): Promise<AdminLineChannel> {
  return request("PATCH", `/admin/line-channels/${encodeURIComponent(id)}`, input);
}

export async function deleteAdminLineChannel(id: string): Promise<AdminLineChannel> {
  return request("DELETE", `/admin/line-channels/${encodeURIComponent(id)}`);
}

export interface AdminImapAccount {
  id: string;
  email: string;
  host: string;
  port: number;
  username: string;
  use_tls: boolean;
  mailbox: string;
  last_uid: number | null;
  last_synced_at: string | null;
  status: string;
  last_error: string | null;
  created_at: string;
  updated_at: string;
}

export async function listAdminImapAccounts(): Promise<{
  imap_accounts: AdminImapAccount[];
}> {
  return request("GET", "/admin/imap-accounts");
}

export async function createAdminImapAccount(input: {
  email: string;
  host: string;
  port?: number | undefined;
  username: string;
  password: string;
  use_tls?: boolean | undefined;
  mailbox?: string | undefined;
}): Promise<AdminImapAccount> {
  return request("POST", "/admin/imap-accounts", input);
}

export async function updateAdminImapAccount(
  id: string,
  input: {
    host?: string | undefined;
    port?: number | undefined;
    username?: string | undefined;
    password?: string | undefined;
    use_tls?: boolean | undefined;
    mailbox?: string | undefined;
    status?: "ok" | "auth_failed" | "unreachable" | "disabled" | undefined;
  },
): Promise<AdminImapAccount> {
  return request("PATCH", `/admin/imap-accounts/${encodeURIComponent(id)}`, input);
}

export async function deleteAdminImapAccount(id: string): Promise<{ ok: boolean }> {
  return request("DELETE", `/admin/imap-accounts/${encodeURIComponent(id)}`);
}

export async function testAdminImapAccount(id: string): Promise<{
  fetched: number;
  matched: number;
  imported: number;
  skipped_duplicate: number;
}> {
  return request("POST", `/admin/imap-accounts/${encodeURIComponent(id)}/test`, {});
}

export type MessageMailType =
  | "normal"
  | "bulk_suspected"
  | "bulk_confirmed"
  | "manual_keep"
  | "manual_purge";

export async function updateMessageClassification(
  id: string,
  input: {
    mail_type?: MessageMailType;
    scrub_after?: string | null;
    extend_days?: number;
  },
): Promise<{
  id: string;
  mail_type: string;
  scrub_after: string | null;
  scrubbed_at: string | null;
}> {
  return request("PATCH", `/messages/${encodeURIComponent(id)}/classification`, input);
}

export async function scrubMessageNow(id: string): Promise<{
  attachments_purged: number;
  bytes_freed: number;
}> {
  return request("POST", `/messages/${encodeURIComponent(id)}/scrub`, {});
}

export async function linkMessageToCustomer(
  id: string,
  input: { customer_id?: string; case_id?: string },
): Promise<{ id: string; customer_id: string | null; case_id: string | null }> {
  return request("PATCH", `/admin/messages/${encodeURIComponent(id)}/link`, input);
}

export async function rematchUnlinkedMessages(): Promise<{
  scanned: number;
  newly_matched: number;
  by_confidence: Record<string, number>;
}> {
  return request("POST", "/admin/messages/rematch", {});
}

export async function listUnlinkedMessages(
  params: {
    limit?: number;
    offset?: number;
  } = {},
): Promise<ReadonlyArray<ApiUnlinkedMessage>> {
  return request("GET", `/admin/messages/unlinked${buildQuery(params)}`);
}

export async function linkUnlinkedMessage(
  id: string,
  input: { case_id?: string; customer_id?: string },
): Promise<ApiUnlinkedMessage> {
  return request("PATCH", `/admin/messages/${encodeURIComponent(id)}/link`, input);
}

export interface GoogleIntegrationStatus {
  connected: boolean;
  calendar_id: string | null;
  scope: string | null;
}

export interface GmailImportResult {
  fetched: number;
  matched: number;
  imported: number;
  skipped_duplicate: number;
}

export function startGoogleConnect(): void {
  window.location.href = "/api/integrations/google/start";
}

export async function disconnectGoogle(): Promise<void> {
  await request<{ ok: boolean }>("POST", "/integrations/google/disconnect", {});
}

export async function getGoogleStatus(): Promise<GoogleIntegrationStatus> {
  return request("GET", "/integrations/google/status");
}

export async function importGmailRecent(): Promise<GmailImportResult> {
  return request("POST", "/integrations/google/gmail/import-recent", {});
}

// ─── Write endpoints (Phase 2-3 lightweight) ─────────────

export interface CreateHistoryInput {
  type: HistoryType;
  content: string;
  ai_summary?: string;
  next_action?: string;
}

export async function createHistory(
  caseId: string,
  input: CreateHistoryInput,
): Promise<ApiHistory> {
  return request("POST", `/cases/${encodeURIComponent(caseId)}/histories`, input);
}

export interface CreateCaseContactInput {
  type: ContactType;
  name: string;
  company?: string;
  phone?: string;
  email?: string;
  memo?: string;
}

export async function addCaseContact(
  caseId: string,
  input: CreateCaseContactInput,
): Promise<ApiCaseContact> {
  return request("POST", `/cases/${encodeURIComponent(caseId)}/contacts`, input);
}

export async function listCaseContacts(caseId: string): Promise<ReadonlyArray<ApiCaseContact>> {
  return request("GET", `/cases/${encodeURIComponent(caseId)}/contacts`);
}

export interface CreateCaseCustomerInput {
  customer_id: string;
  role: CaseCustomerRole;
}

export async function addCaseCustomer(
  caseId: string,
  input: CreateCaseCustomerInput,
): Promise<ApiCaseCustomer> {
  return request("POST", `/cases/${encodeURIComponent(caseId)}/customers`, input);
}

export async function listCaseCustomers(caseId: string): Promise<ReadonlyArray<ApiCaseCustomer>> {
  return request("GET", `/cases/${encodeURIComponent(caseId)}/customers`);
}

export async function listHistories(caseId: string): Promise<ReadonlyArray<ApiHistory>> {
  return request("GET", `/cases/${encodeURIComponent(caseId)}/histories`);
}

export async function listMessages(caseId: string): Promise<ReadonlyArray<ApiMessage>> {
  return request("GET", `/cases/${encodeURIComponent(caseId)}/messages`);
}

export async function listAllMessages(
  filter: ListAllMessagesFilter = {},
): Promise<ReadonlyArray<ApiCrossMessage>> {
  const params = new URLSearchParams();
  if (filter.limit !== undefined) params.set("limit", String(filter.limit));
  if (filter.channel !== undefined) params.set("channel", filter.channel);
  if (filter.direction !== undefined) params.set("direction", filter.direction);
  const qs = params.toString();
  return request("GET", `/messages${qs.length > 0 ? `?${qs}` : ""}`);
}

export async function listSchedules(caseId: string): Promise<ReadonlyArray<ApiSchedule>> {
  return request("GET", `/cases/${encodeURIComponent(caseId)}/schedules`);
}

export interface CreateScheduleInput {
  type: ScheduleType;
  title: string;
  scheduled_at: string;
  location?: string | undefined;
  notes?: string | undefined;
  allow_duplicate?: boolean | undefined;
}

export async function createSchedule(
  caseId: string,
  input: CreateScheduleInput,
): Promise<ApiSchedule> {
  return request("POST", `/cases/${encodeURIComponent(caseId)}/schedules`, input);
}

export async function deleteSchedule(caseId: string, id: string): Promise<void> {
  const casePart = encodeURIComponent(caseId);
  await request<undefined>("DELETE", `/cases/${casePart}/schedules/${encodeURIComponent(id)}`);
}

export interface SendCaseMessageInput {
  to: string;
  cc?: string;
  subject: string;
  body: string;
  from_address?: string | undefined;
}

export async function sendCaseMessage(
  caseId: string,
  input: SendCaseMessageInput,
): Promise<GmailSendResult> {
  return request("POST", `/cases/${encodeURIComponent(caseId)}/messages/send`, input);
}

export interface GmailSendAsAlias {
  sendAsEmail: string;
  displayName: string | null;
  isPrimary: boolean;
  isDefault: boolean;
  verificationStatus: string | null;
}

export async function listGmailSendAsAliases(): Promise<{ aliases: GmailSendAsAlias[] }> {
  return request("GET", "/integrations/google/gmail/send-as");
}

export interface SendCaseLineMessageInput {
  customer_id: string;
  text: string;
}

export interface LineSendResult {
  message_id: string;
  external_id: string;
}

export async function sendCaseLineMessage(
  caseId: string,
  input: SendCaseLineMessageInput,
): Promise<LineSendResult> {
  return request("POST", `/cases/${encodeURIComponent(caseId)}/messages/send-line`, input);
}

export interface ReplyToMessageInput {
  body: string;
  cc?: string;
  subject?: string;
}

export async function replyToMessage(
  caseId: string,
  messageId: string,
  input: ReplyToMessageInput,
): Promise<GmailSendResult> {
  const casePart = encodeURIComponent(caseId);
  const messagePart = encodeURIComponent(messageId);
  return request("POST", `/cases/${casePart}/messages/${messagePart}/reply`, input);
}

export async function replyToCaseMessageAsLine(
  caseId: string,
  messageId: string,
  text: string,
): Promise<LineSendResult> {
  const casePart = encodeURIComponent(caseId);
  const messagePart = encodeURIComponent(messageId);
  return request("POST", `/cases/${casePart}/messages/${messagePart}/reply`, { text });
}

export async function deleteMessage(caseId: string, messageId: string): Promise<void> {
  const casePart = encodeURIComponent(caseId);
  const messagePart = encodeURIComponent(messageId);
  await request<undefined>("DELETE", `/cases/${casePart}/messages/${messagePart}`);
}

export interface CreateTaskInput {
  case_id?: string;
  customer_id?: string;
  title: string;
  description?: string;
  due_date?: string;
  priority?: TaskPriority;
  status?: TaskStatus;
  assigned_to?: string;
  category?: TaskCategory;
}

export async function createTask(input: CreateTaskInput): Promise<ApiTask> {
  return request("POST", "/tasks", input);
}

export interface CreateCustomerInput {
  name: string;
  phone?: string;
  email?: string;
  line_id?: string;
  memo?: string;
  tags?: string;
}

export async function createCustomer(input: CreateCustomerInput): Promise<ApiCustomer> {
  return request("POST", "/customers", input);
}

export interface UpdateCustomerInput {
  name?: string;
  phone?: string;
  email?: string;
  line_id?: string;
  memo?: string;
  tags?: string;
}

export async function updateCustomer(id: string, input: UpdateCustomerInput): Promise<ApiCustomer> {
  return request("PATCH", `/customers/${encodeURIComponent(id)}`, input);
}

export async function deleteCustomer(id: string): Promise<void> {
  await request<undefined>("DELETE", `/customers/${encodeURIComponent(id)}`);
}

export interface CreateCaseInput {
  title: string;
  case_type?: CaseType;
  status?: CaseStatus;
  property_address?: string;
  summary?: string;
  memo?: string;
  customer_id?: string;
  customer_role?: CaseCustomerRole;
}

export async function createCase(input: CreateCaseInput): Promise<ApiCase> {
  return request("POST", "/cases", input);
}

export interface UpdateCaseInput {
  title?: string;
  case_type?: CaseType;
  property_address?: string;
  status?: CaseStatus;
  temperature?: "hot" | "warm" | "cold";
  summary?: string;
  memo?: string;
  mediation_type?: MediationType | null;
  mediation_start_date?: string | null;
  mediation_end_date?: string | null;
}

export async function updateCase(id: string, input: UpdateCaseInput): Promise<ApiCase> {
  return request("PATCH", `/cases/${encodeURIComponent(id)}`, input);
}

export async function deleteCase(id: string): Promise<void> {
  await request<undefined>("DELETE", `/cases/${encodeURIComponent(id)}`);
}

export interface UpdateTaskInput {
  case_id?: string;
  customer_id?: string;
  title?: string;
  description?: string;
  due_date?: string;
  priority?: TaskPriority;
  status?: TaskStatus;
  assigned_to?: string;
  category?: TaskCategory;
}

export async function updateTask(id: string, input: UpdateTaskInput): Promise<ApiTask> {
  return request("PATCH", `/tasks/${encodeURIComponent(id)}`, input);
}

export async function deleteTask(id: string): Promise<void> {
  await request<undefined>("DELETE", `/tasks/${encodeURIComponent(id)}`);
}

export interface UpdateCaseCustomerInput {
  role: CaseCustomerRole;
}

export async function updateCaseCustomer(
  caseId: string,
  id: string,
  input: UpdateCaseCustomerInput,
): Promise<ApiCaseCustomer> {
  const casePart = encodeURIComponent(caseId);
  return request("PATCH", `/cases/${casePart}/customers/${encodeURIComponent(id)}`, input);
}

export async function deleteCaseCustomer(caseId: string, id: string): Promise<void> {
  const casePart = encodeURIComponent(caseId);
  await request<undefined>("DELETE", `/cases/${casePart}/customers/${encodeURIComponent(id)}`);
}

export interface UpdateCaseContactInput {
  name?: string;
  type?: ContactType;
  company?: string;
  phone?: string;
  email?: string;
  memo?: string;
}

export async function updateCaseContact(
  caseId: string,
  id: string,
  input: UpdateCaseContactInput,
): Promise<ApiCaseContact> {
  const casePart = encodeURIComponent(caseId);
  return request("PATCH", `/cases/${casePart}/contacts/${encodeURIComponent(id)}`, input);
}

export async function deleteCaseContact(caseId: string, id: string): Promise<void> {
  const casePart = encodeURIComponent(caseId);
  await request<undefined>("DELETE", `/cases/${casePart}/contacts/${encodeURIComponent(id)}`);
}

export interface UpdateHistoryInput {
  type?: HistoryType;
  content?: string;
  ai_summary?: string;
  next_action?: string;
}

export async function updateHistory(
  caseId: string,
  id: string,
  input: UpdateHistoryInput,
): Promise<ApiHistory> {
  const casePart = encodeURIComponent(caseId);
  return request("PATCH", `/cases/${casePart}/histories/${encodeURIComponent(id)}`, input);
}

export async function deleteHistory(caseId: string, id: string): Promise<void> {
  const casePart = encodeURIComponent(caseId);
  await request<undefined>("DELETE", `/cases/${casePart}/histories/${encodeURIComponent(id)}`);
}

// ─── Survey (面談アンケート) ─────────────────────────────────

export interface ApiSurveyLink {
  id: string;
  token: string;
  kind: string;
  title: string | null;
  case_id: string | null;
  url: string;
  expires_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

export interface ApiSurveyResponse {
  id: string;
  survey_link_id: string;
  kind: string;
  name: string;
  phone: string | null;
  email: string | null;
  answers_json: Record<string, unknown>;
  status: string;
  customer_id: string | null;
  case_id: string | null;
  created_at: string;
}

export interface CreateSurveyLinkInput {
  kind: "sale" | "purchase";
  title?: string;
  expires_in_days?: number;
}

export async function createSurveyLink(input: CreateSurveyLinkInput): Promise<ApiSurveyLink> {
  return request("POST", "/surveys", input);
}

export async function listSurveyLinks(): Promise<ReadonlyArray<ApiSurveyLink>> {
  return request("GET", "/surveys");
}

export async function revokeSurveyLink(id: string): Promise<void> {
  await request<{ revoked: boolean }>("POST", `/surveys/${encodeURIComponent(id)}/revoke`, {});
}

export async function listSurveyResponses(
  status: string,
): Promise<ReadonlyArray<ApiSurveyResponse>> {
  return request("GET", `/survey-responses?status=${encodeURIComponent(status)}`);
}

export async function convertSurveyResponse(
  id: string,
  allowDuplicate?: boolean,
): Promise<{ customer_id: string; case_id: string }> {
  return request("POST", `/survey-responses/${encodeURIComponent(id)}/convert`, {
    allow_duplicate: allowDuplicate,
  });
}

export async function dismissSurveyResponse(id: string): Promise<void> {
  await request<{ dismissed: boolean }>(
    "POST",
    `/survey-responses/${encodeURIComponent(id)}/dismiss`,
    {},
  );
}
